import { TFunction } from 'i18next';
import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import Markdown from 'shared/components/Markdown';
import { SPACINGS } from 'shared/styles/variables';
import styled, { css, ThemeContext } from 'styled-components';

import ImageIdBack from 'images/pictographs/id-back.svg';
import ImageIdFront from 'images/pictographs/id-front.svg';
import ImageLight from 'images/pictographs/light.svg';
import ImageSelfie from 'images/pictographs/selfie.svg';
import ImageUnsupported from 'images/pictographs/unsupported.svg';
import Button, { ButtonType } from 'inquiry/components/Button';
import { IconStyle } from 'lib/interfaces/ThemeSerialized';
import { assertNever } from 'lib/utils/types';
import PositionableButtonContainer from 'verify/components/PositionableButtonContainer/PositionableButtonContainer';
import ThemedParagraph from 'verify/components/ThemedParagraph';
import ThemedSvg from 'verify/components/ThemedSvg';
import ThemedTitle from 'verify/components/ThemedTitle';
import { ConfidenceReasonIdVerify, VerificationGovernmentId } from 'verify/models/verification';
import { MIN_HEIGHTS } from 'verify/styles/variables';

export const TIMEOUT_INTERVAL = 20 * 1000;

/*
 omit min-height and reduce bottom margin
 to remove awkward gap when iconStyle is none
*/
const Header = styled('div')<{ withIcon: boolean }>`
  ${(props) =>
    props.withIcon
      ? css`
          min-height: ${MIN_HEIGHTS.header};
          margin-bottom: ${SPACINGS.base * 4}px;
        `
      : css`
          margin-bottom: ${SPACINGS.base * 2}px;
        `}
`;

const FailureReasonSection = styled('div')`
  margin-bottom: ${SPACINGS.base * 6}px;
`;

const FailureReasonOption = styled('div')`
  display: flex;
  align-items: center;
  margin-bottom: ${SPACINGS.base * 2}px;
`;

const FailureReasonPictograph = styled.div`
  display: flex;
  align-items: center;
  flex-shrink: 0;
  justify-content: space-around;
  margin-right: ${SPACINGS.base * 2}px;

  > img,
  svg {
    height: 50px;
    width: 50px;
  }
`;

interface VerifyGovernmentIdFailedViewProps {
  handleContinue: () => void;
  verification: VerificationGovernmentId;
}

export default function VerifyGovernmentIdFailedView({
  handleContinue,
  verification,
}: VerifyGovernmentIdFailedViewProps) {
  const [t] = useTranslation();
  const { iconStyle } = useContext(ThemeContext);
  const withIcon = iconStyle !== IconStyle.None;

  const failureReasons: ConfidenceReasonIdVerify[] = (
    verification.attributes['entity-confidence-reasons'] || []
  ).filter((reason) => Object.values(ConfidenceReasonIdVerify).includes(reason));
  const chosenFailureReason =
    failureReasons.length === 0 ? ConfidenceReasonIdVerify.Generic : failureReasons[0];

  return (
    <div>
      <Header withIcon={withIcon}>
        <ThemedTitle>
          <Markdown>{t('government-id.failed.title')}</Markdown>
        </ThemedTitle>
        <ThemedParagraph>
          <Markdown>{t('government-id.failed.prompt')}</Markdown>
        </ThemedParagraph>
      </Header>

      <FailureReasonSection>
        <ReasonDisplay withIcon={withIcon} failureReason={chosenFailureReason} t={t} />
      </FailureReasonSection>

      <PositionableButtonContainer>
        <Button
          id='government-id-failed__continue'
          buttonType={ButtonType.Primary}
          onClick={handleContinue}
        >
          {t('government-id.failed.btn-submit')}
        </Button>
      </PositionableButtonContainer>
    </div>
  );
}

interface ReasonDisplayProps {
  withIcon: boolean;
  failureReason: ConfidenceReasonIdVerify;
  t: TFunction;
}
const ReasonDisplay = ({ withIcon, failureReason, t }: ReasonDisplayProps) => {
  let pictograph;
  let description;

  switch (failureReason) {
    case ConfidenceReasonIdVerify.BarcodeNotDetected:
      pictograph = (
        <ThemedSvg vectorEffect='initial'>
          <ImageIdBack />
        </ThemedSvg>
      );
      description = t('government-id.failed.failure-reasons.barcode-missing');
      break;

    case ConfidenceReasonIdVerify.MrzNotDetected:
      pictograph = (
        <ThemedSvg vectorEffect='initial'>
          <ImageIdBack />
        </ThemedSvg>
      );
      description = t('government-id.failed.failure-reasons.mrz-missing');
      break;

    case ConfidenceReasonIdVerify.Blurry:
      pictograph = (
        <ThemedSvg vectorEffect='initial'>
          <ImageIdFront />
        </ThemedSvg>
      );
      description = t('government-id.failed.failure-reasons.blurry');
      break;

    case ConfidenceReasonIdVerify.Glare:
      pictograph = (
        <ThemedSvg>
          <ImageLight />
        </ThemedSvg>
      );
      description = t('government-id.failed.failure-reasons.glare');
      break;

    case ConfidenceReasonIdVerify.PortraitMissing:
      pictograph = (
        <ThemedSvg>
          <ImageSelfie />
        </ThemedSvg>
      );
      description = t('government-id.failed.failure-reasons.missing-portrait');
      break;

    case ConfidenceReasonIdVerify.DoubleFrontDetected:
      pictograph = (
        <ThemedSvg vectorEffect='initial'>
          <ImageIdBack />
        </ThemedSvg>
      );
      description = t('government-id.failed.failure-reasons.id-back');
      break;

    case ConfidenceReasonIdVerify.DisabledIdClass:
      pictograph = (
        <ThemedSvg>
          <ImageUnsupported />
        </ThemedSvg>
      );
      description = t('government-id.failed.failure-reasons.disabled-id-class');
      break;

    case ConfidenceReasonIdVerify.MilitaryId:
      pictograph = (
        <ThemedSvg vectorEffect='initial'>
          <ImageIdFront />
        </ThemedSvg>
      );
      description = t('government-id.failed.failure-reasons.military-id');
      break;

    case ConfidenceReasonIdVerify.Expired:
      pictograph = (
        <ThemedSvg>
          <ImageUnsupported />
        </ThemedSvg>
      );
      description = t('government-id.failed.failure-reasons.expired');
      break;

    case ConfidenceReasonIdVerify.CornerMissing:
      pictograph = (
        <ThemedSvg vectorEffect='initial'>
          <ImageIdFront />
        </ThemedSvg>
      );
      description = t('government-id.failed.failure-reasons.corner-missing');
      break;

    case ConfidenceReasonIdVerify.SelectedCountryMismatch:
      pictograph = (
        <ThemedSvg vectorEffect='initial'>
          <ImageIdFront />
        </ThemedSvg>
      );
      description = t('government-id.failed.failure-reasons.selected-country-mismatch');
      break;

    case ConfidenceReasonIdVerify.FailedAgeComparison:
      pictograph = (
        <ThemedSvg vectorEffect='initial'>
          <ImageIdFront />
        </ThemedSvg>
      );
      description = t('government-id.failed.failure-reasons.failed-age-comparison');
      break;

    case ConfidenceReasonIdVerify.UnprocessableImage:
      pictograph = (
        <ThemedSvg>
          <ImageUnsupported />
        </ThemedSvg>
      );
      description = t('government-id.failed.failure-reasons.unprocessable-image');
      break;

    case ConfidenceReasonIdVerify.PortraitNotClear:
      pictograph = (
        <ThemedSvg>
          <ImageSelfie />
        </ThemedSvg>
      );
      description = t('government-id.failed.failure-reasons.portrait-not-clear');
      break;

    case ConfidenceReasonIdVerify.Generic:
      pictograph = (
        <ThemedSvg vectorEffect='initial'>
          <ImageIdFront />
        </ThemedSvg>
      );
      description = t('government-id.failed.failure-reasons.generic');
      break;

    default:
      assertNever(failureReason);
  }

  return (
    <FailureReasonOption>
      {withIcon && (
        <FailureReasonPictograph data-test='government-id-failed__failure-reason-pictograph'>
          {pictograph}
        </FailureReasonPictograph>
      )}
      <Markdown>{description}</Markdown>
    </FailureReasonOption>
  );
};
